import { Severity, SystemRole } from "node-library/dist/constants";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { sendGETRequest } from "../../requestHelper/sendRequest";

import OrderDetailsContentSkeletonUI from "../mainPages/viewOrders/skeletonUI/orderDetailsContentSkeleton";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import BasicBatchDetailsContent from "../mainPages/batchDetailsPage/basicBatchDetailsContent";
import { FrontendBatchPageTestBatchDetails } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	batchId: string;
	user: FrontendUserInfo | undefined;

	goBackToPreviousPage: (reload: boolean) => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function BasicViewBatchDetailsContentWrapper(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		batchId,
		goBackToPreviousPage,
		company,
		setCompany,
		setUnsavedChanges,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);

	const [selectedBatchDetails, setSelectedBatchDetails] =
		useState<FrontendBatchPageTestBatchDetails>();

	const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
	const [specDirectories, setSpecDirectories] = useState<
		Array<FrontendSampleSpecificationDirectory>
	>([]);

	const doesUserHaveAccess: boolean = props.user
		? hasPermission(
				props.user?.role?.permissions as PermissionMap,
				Permissions.batches.getDetails,
			)
		: false;

	useEffect(() => {
		async function loadBatchDetails() {
			setLoading(true);
			try {
				const response = await sendGETRequest(
					"/sample-test-batches/get-batch-details-by-id",
					{
						batchId,
					},
				);
				const frontendBatchDetails: FrontendBatchPageTestBatchDetails =
					response?.data?.frontendBatchDetails;
				setSelectedBatchDetails(frontendBatchDetails);
				const specDirectories: Array<FrontendSampleSpecificationDirectory> =
					response?.data?.specDirectories;
				setSpecDirectories(specDirectories);
			} catch (error: any) {
				console.error(error);
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			} finally {
				setLoading(false);
			}
		}

		if (batchId && doesUserHaveAccess) {
			loadBatchDetails();
		}
	}, [batchId]);

	function gotoInstanceDetailsPage() {
		setCurrentPageIndex(0);
	}

	let currentComponent: JSX.Element = <></>;
	switch (currentPageIndex) {
		case 0:
			currentComponent = !selectedBatchDetails ? (
				<OrderDetailsContentSkeletonUI />
			) : (
				<BasicBatchDetailsContent
					company={company}
					setCompany={setCompany}
					batchDetails={
						selectedBatchDetails as FrontendBatchPageTestBatchDetails
					}
					user={props.user as FrontendUserInfo}
					goBackToPreviousPage={goBackToPreviousPage}
					specDirectories={specDirectories}
					updateSpecification={(updatedSpec: FrontendSampleSpecification) => {
						setSelectedBatchDetails({
							...selectedBatchDetails,
							instances: selectedBatchDetails.instances.map((inst) =>
								inst.specification?.specificationId ===
								updatedSpec.specificationId
									? { ...inst, specification: updatedSpec }
									: inst,
							),
						});
					}}
				></BasicBatchDetailsContent>
			);
			break;
	}

	return (
		<div style={{ margin: "0 auto" }}>
			{doesUserHaveAccess ? (
				<>{currentComponent}</>
			) : (
				<div>{translation("YouDoNotHavePermissionToViewContent")}</div>
			)}
		</div>
	);
}
